import { useState } from "react";
import { Link } from "react-router-dom";
import { useProfile } from "@/hooks/useApi";
import {
  SettingsPageHeader,
  SettingsSection,
  SettingsTabs,
} from "@/components/settings/settings-ui";

const TABS = [
  "Overview",
  "Key benefits & outcomes",
  "Unique characteristics",
  "Other details",
] as const;

export function SettingsContentCenterPage() {
  const { data: profile } = useProfile();
  const [tab, setTab] = useState<(typeof TABS)[number]>("Overview");
  const company = profile?.name?.trim() || "Your company";
  const narrative = profile?.narrative?.trim();
  const superpowers = profile?.superpowers ?? [];
  const proofPoints = profile?.proofPoints ?? [];
  const dealbreakers = profile?.dealbreakers ?? [];

  return (
    <div>
      <SettingsPageHeader
        title="AI context center"
        description="Better context = better AI outputs. Correcting errors here prevents the AI from sending inaccurate info to leads and accounts."
      />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted/20 px-4 py-3 text-sm">
        <p className="text-muted-foreground">
          Welcome to Context Center. Looking for your legacy profile?
        </p>
        <Link to="/settings/account" className="font-medium text-brand hover:underline">
          View legacy profile
        </Link>
      </div>

      <SettingsSection
        title="Your company information"
        description="The core facts and brand voice applied to every AI interaction."
        action={
          <Link to="/narrative" className="text-xs font-medium text-brand hover:underline">
            Edit
          </Link>
        }
      >
        <div className="mb-4 flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-md bg-brand/15 text-sm font-bold text-brand">
            {company.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">{company}</p>
            <p className="text-xs text-muted-foreground">
              {profile?.email ? `Synced from ${profile.email.split("@")[1] ?? "profile"}` : "From profile"}
            </p>
          </div>
        </div>

        <SettingsTabs tabs={[...TABS]} active={tab} onChange={(t) => setTab(t as typeof tab)} />

        {tab === "Overview" && (
          <p className="whitespace-pre-wrap text-sm text-foreground">
            {narrative || "Add a company narrative on the Narrative page to power AI outputs."}
          </p>
        )}
        {tab === "Key benefits & outcomes" && (
          <ul className="list-disc space-y-1 pl-5 text-sm">
            {(proofPoints.length ? proofPoints : ["Add proof points on Narrative / Profile."]).map(
              (p) => (
                <li key={p}>{p}</li>
              ),
            )}
          </ul>
        )}
        {tab === "Unique characteristics" && (
          <ul className="list-disc space-y-1 pl-5 text-sm">
            {(superpowers.length ? superpowers : ["Add superpowers on Profile."]).map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
        {tab === "Other details" && (
          <ul className="list-disc space-y-1 pl-5 text-sm">
            {(dealbreakers.length ? dealbreakers : ["Add dealbreakers on Profile."]).map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
      </SettingsSection>

      <SettingsSection
        title="Your products & services"
        description="Specific details the AI uses to pitch different parts of your platform."
        action={
          <Link to="/narrative" className="text-xs font-medium text-brand hover:underline">
            Add
          </Link>
        }
      >
        <div className="grid gap-2 sm:grid-cols-2">
          {[
            { title: "Narrative", href: "/narrative", hint: "Seller story & ICP summary" },
            { title: "AI providers", href: "/settings/ai", hint: "Model keys & usage" },
            { title: "Sequence prompts", href: "/settings/sequence", hint: "Engage templates" },
            { title: "Personas", href: "/settings/personas", hint: "Ideal customer profile" },
            { title: "CLI", href: "/settings/cli", hint: "API keys for automations" },
            { title: "Signals", href: "/settings/signals", hint: "Enrichment providers" },
          ].map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className="rounded-md border border-border px-3 py-3 transition hover:bg-muted/10"
            >
              <p className="text-sm font-medium text-foreground">{item.title}</p>
              <p className="text-xs text-muted-foreground">{item.hint}</p>
            </Link>
          ))}
        </div>
      </SettingsSection>
    </div>
  );
}
